import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Checkbox,
    FormControlLabel,
    Grid,
    TextField,
    Autocomplete,
    Paper,
    Typography,
    Chip,
    Stack,
} from '@mui/material';
import useStyles from '../styles';
import { updateFrom } from '../../../redux/Actions/createInstance.action';
import { ADD_AGENTIC_SUITE } from '../../../redux/Constants/createInstance.constants';

const AGENTS = [
    {
        aiAgentKey: 'aiSupportAgent',
        label: 'AI Support Agent',
        hasLicenses: false,
        crmSupport: false,
    },
    {
        aiAgentKey: 'aiCaseQAAgent',
        label: 'AI Case QA Agent',
        hasLicenses: false,
        crmSupport: true,
    },
    {
        aiAgentKey: 'aiAgentPartner',
        label: 'AI Agent Partner',
        hasLicenses: true,
        crmSupport: true,
    },
    {
        aiAgentKey: 'aiKnowledgeAgent',
        label: 'AI Knowledge Agent',
        hasLicenses: false,
        crmSupport: true,
    },
];

const PLATFORMS = {
    salesforce: 'Salesforce',
    zendesk: 'Zendesk',
    servicenow: 'Service Now',
};

const PLATFORM_OPTIONS = Object.entries(PLATFORMS).map(([key, label]) => ({
    key,
    label,
}));

const DEFAULT_SUITE = AGENTS.map(({ aiAgentKey }) => ({
    aiAgentKey,
    enabled: false,
    expiry: null,
    licensesCount: null,
    crms: []
}));

export default function AgenticSuite({ val }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [suite, setSuite] = useState(DEFAULT_SUITE);

    useEffect(() => {
        if (val && Array.isArray(val.agenticSuite) && val.agenticSuite.length) {
            setSuite(val.agenticSuite);
        }
    }, [val]);

    useEffect(() => {
        dispatch(updateFrom(suite, ADD_AGENTIC_SUITE));
    }, [suite, dispatch]);

    const getAgent = (key) => suite.find((a) => a.aiAgentKey === key) || {};

    const updateAgent = (key, changes) =>
        setSuite((prev) => prev.map((a) => (a.aiAgentKey === key ? { ...a, ...changes } : a)));

    const handleCheckbox = (key) => (e) => {
        const isChecked = e.target.checked;
        const changes = { enabled: isChecked };
        if (!isChecked) {
            changes.licensesCount = null;
            changes.crms = [];
        }
        updateAgent(key, changes);
    };

    const handleLicenses = (key) => (event) => {
        const { value } = event.target;
        if (value === '') {
            updateAgent(key, { licensesCount: '' });
            return;
        }
        const numericValue = Math.max(0, parseInt(value, 10));
        if (!Number.isNaN(numericValue)) {
            updateAgent(key, { licensesCount: value });
        }
    };

    const handleCrmChange = (key) => (event, newValue) => {
        // Extract pure key strings ['salesforce', 'zendesk'] for state
        const selectedKeys = newValue.map((item) => item.key);
        updateAgent(key, { crms: selectedKeys });
    };

    return (
        <div className={classes.formContainer}>
            <h1 className={classes.heading}>Agentic Suite</h1>
            <Stack spacing={2} style={{ padding: '0 16px 16px' }}>
                {AGENTS.map(({ aiAgentKey, label, hasLicenses, crmSupport }) => {
                    const agent = getAgent(aiAgentKey);
                    const isEnabled = !!agent.enabled;

                    // FIX: Map agent's stored keys to options objects FOR THIS SPECIFIC AGENT
                    const selectedCRMs = (agent.crms || []).map((key) => ({
                        key,
                        label: PLATFORMS[key] || key,
                    }));

                    return (
                        <Paper
                            key={aiAgentKey}
                            variant="outlined"
                            style={{
                                padding: '16px',
                                backgroundColor: isEnabled ? '#fafafa' : '#ffffff',
                                transition: 'background-color 0.2s ease',
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                {/* Agent Selector Checkbox */}
                                <Grid item xs={12} sm={4} md={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isEnabled}
                                                onChange={handleCheckbox(aiAgentKey)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="body1" fontWeight={isEnabled ? 600 : 400}>
                                                {label}
                                            </Typography>
                                        }
                                    />
                                </Grid>

                                {/* User Licenses */}
                                <Grid item xs={12} sm={3} md={3}>
                                    {hasLicenses ? (
                                        <TextField
                                            type="number"
                                            label="User Licenses"
                                            size="small"
                                            disabled={!isEnabled}
                                            value={agent.licensesCount ?? ''}
                                            onChange={handleLicenses(aiAgentKey)}
                                            onFocus={(e) => e.target.select()}
                                            fullWidth
                                        />
                                    ) : null}
                                </Grid>

                                {/* CRM Platforms Multi-Select */}
                                <Grid item xs={12} sm={5} md={6}>
                                    {crmSupport ? (
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            disabled={!isEnabled}
                                            options={PLATFORM_OPTIONS}
                                            value={selectedCRMs}
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, value) => option.key === value.key}
                                            onChange={handleCrmChange(aiAgentKey)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="CRM Platforms"
                                                    placeholder={isEnabled ? 'Select platforms' : ''}
                                                />
                                            )}
                                            renderTags={(selected, getTagProps) =>
                                                selected.map((option, index) => {
                                                    const { key, ...tagProps } = getTagProps({ index });
                                                    return (
                                                        <Chip
                                                            key={option.key}
                                                            label={option.label}
                                                            size="small"
                                                            {...tagProps}
                                                        />
                                                    );
                                                })
                                            }
                                        />
                                    ) : null}
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Stack>
        </div>
    );
}
